import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '../context/WalletContext';
import { subscriptionAPI } from '../api/endpoints';
import { PUNTER_PLUS_PLANS } from '../utils/constants';
import { formatXPUNT, formatDate } from '../utils/helpers';
import { 
  FaStar, FaCheck, FaArrowLeft, FaCrown, 
  FaRocket, FaCalendar, FaWallet, FaGem,
  FaLightbulb, FaShieldAlt, FaTrophy, FaUsers,
  FaChartLine, FaLock, FaUnlock, FaFire,
  FaClock, FaDollarSign, FaCreditCard, FaArrowRight,
  FaPlus, FaHistory, FaHandshake
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './Subscription.css';

const Subscription = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, updateUser } = useAuth();
  const { balance, fetchBalance } = useWallet();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('bi-annual');
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [planDetails, setPlanDetails] = useState(null);
  const [showExtendModal, setShowExtendModal] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchSubscriptionInfo();
  }, [isAuthenticated, navigate]);

  const fetchSubscriptionInfo = async () => {
    setLoading(true);
    try {
      const response = await subscriptionAPI.getInfo();
      setSubscriptionInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    const plan = PUNTER_PLUS_PLANS[planId.toUpperCase().replace('-', '_')];
    setPlanDetails(plan);
  };

const handleUpgrade = () => {
  const plan = PUNTER_PLUS_PLANS[selectedPlan.toUpperCase().replace('-', '_')];
  if (!plan) return;

  if (balance < plan.price) {
    toast.error(`Insufficient balance. You need ${formatXPUNT(plan.price)}`);
    return;
  }

  setPlanDetails(plan);
  setShowConfirmation(true);
};


  const handleExtend = () => {
    const plan = PUNTER_PLUS_PLANS[selectedPlan.toUpperCase().replace('-', '_')];
    if (!plan) return;

    if (balance < plan.price) {
      toast.error(`Insufficient balance. You need ${formatXPUNT(plan.price)}`);
      return;
    }

    setPlanDetails(plan);
    setShowExtendModal(true);
  };

  const confirmUpgrade = async () => {
  const plan = PUNTER_PLUS_PLANS[selectedPlan.toUpperCase().replace('-', '_')];
  if (!plan) return;

  setProcessing(true);
  try {
    // Send the correct duration format to match backend
    const durationMap = {
      'monthly': '1 month',
      'bi-annual': '6 months',
      'yearly': '1 year'
    };
    
    const response = await subscriptionAPI.upgrade({ 
      duration: durationMap[selectedPlan] || '1 month'
    });
    
    if (response.data.success) {
      toast.success(response.data.message || `🎉 Successfully upgraded to Punter+!`);
      updateUser({ 
        punter_plus: true,
        punter_plus_expiry: response.data.expiry_date,
        punter_plus_status: 'active'
      });
      await fetchBalance();
      await fetchSubscriptionInfo();
      setShowConfirmation(false);
      navigate('/dashboard');
    }
  } catch (error) {
    toast.error(error.response?.data?.message || 'Failed to upgrade');
  } finally {
    setProcessing(false);
  }
};

  const getPlanIcon = (planId) => {
    const icons = {
      monthly: <FaCalendar />,
      'bi-annual': <FaRocket />,
      yearly: <FaCrown />,
    };
    return icons[planId] || <FaStar />;
  };

  const getPlanColor = (planId) => {
    const colors = {
      monthly: '#3498db',
      'bi-annual': '#f7971e',
      yearly: '#9b59b6',
    };
    return colors[planId] || '#f7971e';
  };

  const getPlanBorderColor = (planId) => {
    const colors = {
      monthly: 'rgba(52, 152, 219, 0.3)',
      'bi-annual': 'rgba(247, 151, 30, 0.3)',
      yearly: 'rgba(155, 89, 182, 0.3)',
    };
    return colors[planId] || 'rgba(247, 151, 30, 0.3)';
  };

  const isActive = subscriptionInfo?.is_punter_plus && subscriptionInfo?.is_active;
  const canExtend = isActive && subscriptionInfo?.can_upgrade;

  if (loading) {
    return (
      <div className="subscription-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="subscription-page">
      <button onClick={() => navigate(-1)} className="back-button">
        <FaArrowLeft /> Back
      </button>

      {/* Header */}
      <div className="subscription-header">
        <div className="header-content">
          <h1>
            <FaCrown className="header-icon" /> Punter+ Premium
          </h1>
          <p className="header-subtitle">
            Unlock premium features and take your betting to the next level
          </p>
        </div>
        {isActive && (
          <div className="active-status-badge">
            <FaCheck /> Active
            {canExtend && (
              <button 
                className="extend-btn"
                onClick={handleExtend}
              >
                <FaPlus /> Extend
              </button>
            )}
          </div>
        )}
      </div>

      {/* Current Status */}
      <div className={`status-card ${isActive ? 'active' : 'inactive'}`}>
        <div className="status-icon">
          {isActive ? <FaCrown className="active-icon" /> : <FaStar className="inactive-icon" />}
        </div>
        <div className="status-content">
          <h3>{isActive ? 'You are a Punter+ Member! 🎉' : 'Upgrade to Punter+'}</h3>
          <p className="status-text">
            {isActive 
              ? `Your subscription is active until ${formatDate(subscriptionInfo?.expiry_date)}`
              : 'Get access to premium features and start earning more'
            }
          </p>
          {isActive && subscriptionInfo?.days_remaining !== undefined && (
            <div className="days-remaining">
              <FaClock /> {subscriptionInfo.days_remaining} days remaining
            </div>
          )}
        </div>
        {isActive && canExtend && (
          <button 
            className="btn btn-primary btn-sm"
            onClick={handleExtend}
          >
            <FaPlus /> Extend Subscription
          </button>
        )}
      </div>

      {/* Benefits Section */}
      <div className="benefits-section">
        <h2>
          <FaStar className="section-icon" /> Why Go Punter+?
        </h2>
        <div className="benefits-grid">
          <div className="benefit-card">
            <div className="benefit-icon-wrapper gold">
              <FaLock className="benefit-icon" />
            </div>
            <h4>Private Bet Codes</h4>
            <p>Create exclusive bet codes for your subscribers and build a following</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon-wrapper blue">
              <FaTrophy className="benefit-icon" />
            </div>
            <h4>Create Competitions</h4>
            <p>Host your own prediction competitions and engage the community</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon-wrapper purple">
              <FaHandshake className="benefit-icon" />
            </div>
            <h4>H2H Games</h4>
            <p>Create head-to-head challenges and compete with other punters</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon-wrapper orange">
              <FaChartLine className="benefit-icon" />
            </div>
            <h4>Rollover Games</h4>
            <p>Create sequential bet code challenges for your subscribers</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon-wrapper green">
              <FaShieldAlt className="benefit-icon" />
            </div>
            <h4>Priority Support</h4>
            <p>Get priority support from our team whenever you need help</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon-wrapper pink">
              <FaGem className="benefit-icon" />
            </div>
            <h4>Creator Subscription</h4>
            <p>Create subscription for your members, charge them to access your premium contents</p>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      {!isActive && (
        <div className="pricing-section">
          <div className="pricing-header">
            <h2>Choose Your Plan</h2>
            <p className="pricing-subtitle">
              Select the plan that fits your needs and start enjoying premium features
            </p>
          </div>

          <div className="plans-grid">
            {Object.entries(PUNTER_PLUS_PLANS).map(([key, plan]) => {
              const isSelected = selectedPlan === key;
              const isPopular = plan.popular;
              const color = getPlanColor(key);
              const borderColor = getPlanBorderColor(key);

              return (
                <div 
                  key={key}
                  className={`plan-card ${isSelected ? 'selected' : ''} ${isPopular ? 'popular' : ''}`}
                  onClick={() => handlePlanSelect(key)}
                  style={{ 
                    borderColor: isSelected ? color : 'rgba(255,255,255,0.05)',
                    boxShadow: isSelected ? `0 0 40px ${borderColor}` : 'none'
                  }}
                >
                  {isPopular && (
                    <div className="popular-badge">
                      <FaFire /> Most Popular
                    </div>
                  )}
                  <div className="plan-header" style={{ color }}>
                    {getPlanIcon(key)}
                    <h3>{plan.label}</h3>
                  </div>
                  <div className="plan-price">
                    <span className="amount">{formatXPUNT(plan.price)}</span>
                    <span className="period">/ {plan.duration}</span>
                  </div>
                  {plan.savings > 0 && (
                    <div className="plan-savings">
                      Save {plan.savings}%
                    </div>
                  )}
                  <div className="plan-features">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="feature-item">
                        <FaCheck className="feature-check" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="plan-total">
                    <span>Total: {formatXPUNT(plan.price)}</span>
                  </div>
                  {isSelected && (
                    <div className="plan-selected-badge">
                      <FaCheck /> Selected
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Wallet Balance & Upgrade */}
          <div className="upgrade-section">
            <div className="upgrade-info">
              <div className="wallet-balance">
                <FaWallet className="wallet-icon" />
                <span>Available Balance: <strong>{formatXPUNT(balance)}</strong></span>
              </div>
              <div className="plan-summary">
                Selected: <strong>{PUNTER_PLUS_PLANS[selectedPlan.toUpperCase().replace('-', '_')]?.label}</strong>
                {' '}· Price: <strong>{formatXPUNT(PUNTER_PLUS_PLANS[selectedPlan.toUpperCase().replace('-', '_')]?.price)}</strong>
              </div>
              {balance < PUNTER_PLUS_PLANS[selectedPlan.toUpperCase().replace('-', '_')]?.price && (
                <div className="insufficient-warning">
                  ⚠️ Insufficient balance. You need {formatXPUNT(PUNTER_PLUS_PLANS[selectedPlan.toUpperCase().replace('-', '_')]?.price)}
                  <Link to="/wallet" className="fund-link">
                    <FaCreditCard /> Fund Wallet
                  </Link>
                </div>
              )}
            </div>
            <button 
              className="btn btn-primary btn-lg upgrade-btn"
              onClick={handleUpgrade}
              disabled={balance < PUNTER_PLUS_PLANS[selectedPlan.toUpperCase().replace('-', '_')]?.price}
            >
              <FaRocket /> Upgrade Now
              <FaArrowRight />
            </button>
          </div>

          {/* Payment Options */}
          <div className="payment-options">
            <p className="payment-label">Secure payment via</p>
            <div className="payment-icons">
              <span className="payment-icon">💳 Card</span>
              <span className="payment-icon">🏦 Bank</span>
              <span className="payment-icon">📱 USSD</span>
              <span className="payment-icon">₿ Crypto</span>
            </div>
            <p className="payment-note">
              Powered by Flutterwave · Your funds are secure
            </p>
          </div>
        </div>
      )}

      {/* Already Active */}
      {isActive && (
        <div className="active-section">
          <div className="active-card">
            <div className="active-icon-wrapper">
              <FaCrown className="active-icon" />
            </div>
            <h2>You're a Punter+ Member! 🎉</h2>
            <p>You have full access to all premium features and benefits</p>
            
            <div className="active-features-grid">
              <div className="active-feature">
                <FaCheck className="feature-check" />
                <span>Private Bet Codes</span>
              </div>
              <div className="active-feature">
                <FaCheck className="feature-check" />
                <span>Create Competitions</span>
              </div>
              <div className="active-feature">
                <FaCheck className="feature-check" />
                <span>H2H Games</span>
              </div>
              <div className="active-feature">
                <FaCheck className="feature-check" />
                <span>Rollover Games</span>
              </div>
              <div className="active-feature">
                <FaCheck className="feature-check" />
                <span>Priority Support</span>
              </div>
              <div className="active-feature">
                <FaCheck className="feature-check" />
                <span>Premium Badge</span>
              </div>
            </div>

            <div className="active-actions">
              <Link to="/creator/dashboard" className="btn btn-primary">
                <FaRocket /> Creator Dashboard
              </Link>
              <Link to="/bet-codes/create" className="btn btn-secondary">
                <FaLock /> Create Private Bet
              </Link>
            </div>

            {canExtend && (
              <div className="extend-section">
                <p>Want to extend your subscription?</p>
                <button 
                  className="btn btn-outline btn-sm"
                  onClick={handleExtend}
                >
                  <FaPlus /> Extend Now
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* FAQ Section */}
      <div className="faq-section">
        <h2>❓ Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>What is Punter+?</h4>
            <p>Punter+ is our premium subscription that unlocks advanced features like private bet codes, competitions, H2H games, and more.</p>
          </div>
          <div className="faq-item">
            <h4>How do I upgrade?</h4>
            <p>Select a plan above and click "Upgrade Now". The amount will be deducted from your $XPUNT wallet balance.</p>
          </div>
          <div className="faq-item">
            <h4>Can I cancel anytime?</h4>
            <p>Yes, you can cancel your subscription at any time. You'll retain benefits until the end of your current billing period.</p>
          </div>
          <div className="faq-item">
            <h4>What happens when I cancel?</h4>
            <p>Your Punter+ benefits will continue until your current subscription expires. After that, you'll revert to the free plan.</p>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && planDetails && (
        <div className="confirmation-overlay">
          <div className="confirmation-modal">
            <div className="confirmation-header">
              <FaCrown className="confirmation-icon" />
              <h2>Confirm Upgrade</h2>
            </div>
            <div className="confirmation-body">
              <p>You are about to upgrade to <strong>Punter+ {planDetails.label}</strong></p>
              <div className="confirmation-plan-details">
                <div className="detail-row">
                  <span>Plan</span>
                  <span><strong>{planDetails.label}</strong></span>
                </div>
                <div className="detail-row">
                  <span>Duration</span>
                  <span>{planDetails.duration}</span>
                </div>
                <div className="detail-row highlight">
                  <span>Price</span>
                  <span><strong>{formatXPUNT(planDetails.price)}</strong></span>
                </div>
                <div className="detail-row">
                  <span>Wallet Balance</span>
                  <span>{formatXPUNT(balance)}</span>
                </div>
                <div className="detail-row total">
                  <span>Remaining Balance</span>
                  <span>{formatXPUNT(balance - planDetails.price)}</span>
                </div>
              </div>
              <div className="confirmation-warning">
                <FaShieldAlt /> This amount will be deducted from your wallet balance.
              </div>
            </div>
            <div className="confirmation-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowConfirmation(false)}
                disabled={processing}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={confirmUpgrade}
                disabled={processing}
              >
                {processing ? <LoadingSpinner size="small" /> : 'Confirm Upgrade'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extend Modal */}
      {showExtendModal && planDetails && (
        <div className="confirmation-overlay">
          <div className="confirmation-modal">
            <div className="confirmation-header">
              <FaPlus className="confirmation-icon" />
              <h2>Extend Subscription</h2>
            </div>
            <div className="confirmation-body">
              <p>You are extending your <strong>Punter+ {planDetails.label}</strong> subscription</p>
              <div className="confirmation-plan-details">
                <div className="detail-row">
                  <span>Current Plan</span>
                  <span><strong>{planDetails.label}</strong></span>
                </div>
                <div className="detail-row">
                  <span>Extension Duration</span>
                  <span>{planDetails.duration}</span>
                </div>
                <div className="detail-row">
                  <span>Current Expiry</span>
                  <span>{formatDate(subscriptionInfo?.expiry_date)}</span>
                </div>
                <div className="detail-row highlight">
                  <span>New Expiry</span>
                  <span><strong>
                    {subscriptionInfo?.expiry_date 
                      ? formatDate(new Date(new Date(subscriptionInfo.expiry_date).getTime() + planDetails.durationDays * 24 * 60 * 60 * 1000))
                      : 'N/A'}
                  </strong></span>
                </div>
                <div className="detail-row">
                  <span>Price</span>
                  <span><strong>{formatXPUNT(planDetails.price)}</strong></span>
                </div>
                <div className="detail-row total">
                  <span>Remaining Balance</span>
                  <span>{formatXPUNT(balance - planDetails.price)}</span>
                </div>
              </div>
              <div className="confirmation-warning">
                <FaShieldAlt /> This amount will be deducted from your wallet balance.
              </div>
            </div>
            <div className="confirmation-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowExtendModal(false)}
                disabled={processing}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={confirmUpgrade}
                disabled={processing}
              >
                {processing ? <LoadingSpinner size="small" /> : 'Confirm Extension'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscription;